// Adds comment toggling and auto-indenting
import "prism-react-editor/languages/json";

import "prism-react-editor/layout.css";
import "prism-react-editor/themes/github-dark.css";

// Required by the basic setup
import "prism-react-editor/search.css";

import { Editor } from "prism-react-editor";
import { BasicSetup } from "prism-react-editor/setups";
import { useEffect, useState } from "react";
import { Button, Col, Container, Modal, Row } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useAppSelector } from "../../store";
import { templatesActions } from "../../store/templates-slice";
import { queryClient } from "../../utils/http/queryClient";

function RenderingVariablesModal({
  showModal,
  setShowModal,
}: {
  showModal: boolean;
  setShowModal: (show: boolean) => void;
}) {
  const dispatch = useDispatch();
  const renderingVariables = useAppSelector((state) => state.templates.renderingVariables);
  const selectedPath = useAppSelector((state) => state.templates.selectedPath);
  const selectedVersion = useAppSelector((state) => state.templates.selectedVersion);

  const [initialVariables, setInitialVariables] = useState<string>(renderingVariables);
  const [currentVariables, setCurrentVariables] = useState<string>(renderingVariables);

  // Sync initial variables when modal opens
  useEffect(() => {
    if (showModal) {
      setInitialVariables(renderingVariables);
      setCurrentVariables(renderingVariables);
    }
  }, [showModal]);

  const handleSaveVariables = () => {
    try {
      // Validate JSON
      JSON.parse(currentVariables);

      // Save to Redux state
      dispatch(templatesActions.setRenderingVariables(currentVariables));

      // Invalidate the template render query to trigger a re-render with new variables
      queryClient.invalidateQueries({
        queryKey: ["templateRender", selectedPath!.namespace, selectedPath!.name, selectedVersion],
      });

      setShowModal(false);
    } catch (error) {
      console.error("Failed to parse JSON:", error);
      alert("Invalid JSON format. Please check your rendering variables.");
    }
  };

  const closeModal = () => {
    setShowModal(false);
  };

  const updateVariables = (fields: string) => {
    setCurrentVariables(fields);
  };

  return (
    <Modal size="xl" show={showModal} onHide={closeModal}>
      <Modal.Header closeButton>
        <Modal.Title>Rendering Variables</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Container>
          <Row className="mb-3 align-items-center">
            <Col xs={8}>
              <p className="mb-0">
                Edit the JSON object below to customize the rendering variables passed to the template.
                <br />
                This allows you to test how the template renders with different data.
              </p>
            </Col>
            <Col xs={4} className="text-end">
              <Button variant="secondary" onClick={closeModal} className="me-2">
                Close
              </Button>
              <Button variant="primary" onClick={handleSaveVariables}>
                Save Variables
              </Button>
            </Col>
          </Row>
          <Row>
            <Col>
              <Editor
                language="json"
                value={initialVariables}
                onUpdate={(text) => updateVariables(text)}
                wordWrap={false}
              >
                {(editor) => <BasicSetup editor={editor} />}
              </Editor>
            </Col>
          </Row>
        </Container>
      </Modal.Body>
    </Modal>
  );
}

export default RenderingVariablesModal;
